/**
 * CPG AI Lab — Dashboard
 *
 * Rebrand-in-place of the former "AI Studio". Same subscription/gate as
 * before (the $25/mo add-on is preserved so we don't break billing), but
 * the surface is reframed around a modular Lab: Brand Profile foundation,
 * a growing catalog of 5-section Toolkits, and Warren AI at the top.
 *
 * Route: `/ai-lab` (canonical), `/ai-center` still resolves for legacy
 * bookmarks — see App.js.
 */
import React from "react";
import { Link } from "react-router-dom";
import AppShell from "../../components/AppShell";
import PageHero from "../../components/PageHero";
import {
  Layers, Palette, Sparkles, ArrowRight, ClipboardList,
  ChevronRight, Wand2, Bot, ShieldCheck,
} from "lucide-react";

const TOOLKITS = [
  {
    id: "packaging",
    title: "Packaging AI",
    blurb: "Hero mockups + shelf-ready visuals guided by your Brand Profile.",
    to: "/ai-lab/packaging",
    icon: Layers,
    status: "live",
    tag: "Toolkit 01",
  },
  {
    id: "logo",
    title: "Logo Designer",
    blurb: "Wordmark + monogram concepts anchored to your positioning.",
    to: "/ai/logo-designer",
    icon: Palette,
    status: "live",
    tag: "Legacy",
  },
  {
    id: "meta-ads",
    title: "Meta Ads Manager",
    blurb: "Ad copy + creative briefs for Facebook & Instagram.",
    to: "/ai/meta-ads-manager",
    icon: Sparkles,
    status: "live",
    tag: "Legacy",
  },
  {
    id: "reddit-ads",
    title: "Reddit Ads Manager",
    blurb: "Community-first ad copy that reads like a Redditor wrote it.",
    to: "/ai/reddit-ads-manager",
    icon: Sparkles,
    status: "live",
    tag: "Legacy",
  },
  {
    id: "ads-designer",
    title: "Ads Designer",
    blurb: "Static ad creative concepts across every major placement.",
    to: "/ai/ads-designer",
    icon: Palette,
    status: "live",
    tag: "Legacy",
  },
];

export default function AILabDashboard() {
  return (
    <AppShell>
      <div className="p-4 md:p-8" data-testid="ai-lab-dashboard">
        <PageHero
          eyebrow="Introducing"
          title="CPG AI Lab"
          subtitle="A modular AI toolkit for CPG founders. Author your brand once, then generate on-brand packaging, formulations, ads, and copy in whichever best-in-class tool you prefer."
        />

        {/* Foundation — Brand Profile */}
        <section className="mt-6 rounded-2xl overflow-hidden border-2 border-shell-side/30 bg-white"
                 data-testid="ai-lab-foundation">
          <div className="p-5 md:p-6 flex flex-col md:flex-row gap-5 md:items-center">
            <div className="shrink-0 grid place-items-center w-14 h-14 rounded-2xl bg-shell-side/10 text-shell-side">
              <ShieldCheck className="w-7 h-7" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] uppercase tracking-widest text-shell-side font-bold">
                Foundation · Start here
              </p>
              <h2 className="font-display text-2xl mt-1">Brand Profile</h2>
              <p className="mt-1 text-sm text-muted-foreground max-w-prose">
                Author brand name, category, positioning, and target consumer once.
                Every Lab toolkit reads from here so nothing has to be re-explained
                per generation.
              </p>
            </div>
            <Link to="/ai-lab/brand-profile" data-testid="ai-lab-open-brand-profile"
              className="btn-primary text-sm inline-flex items-center gap-1.5 whitespace-nowrap">
              Author brand profile <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* Toolkits grid */}
        <section className="mt-8" data-testid="ai-lab-toolkits">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl">Toolkits</h2>
            <Link to="/assistant" className="text-xs text-shell-side font-semibold inline-flex items-center gap-1"
              data-testid="ai-lab-open-assistant">
              <Bot className="w-3.5 h-3.5" /> Chat with Morgan
            </Link>
          </div>
          <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {TOOLKITS.map((t) => (
              <ToolkitCard key={t.id} {...t} />
            ))}
          </div>
        </section>

        {/* Explainer */}
        <section className="mt-10 rounded-2xl border border-shell-border p-5 md:p-6 bg-white">
          <p className="text-[10px] uppercase tracking-widest text-shell-side font-bold flex items-center gap-1.5">
            <Wand2 className="w-3.5 h-3.5" /> How every toolkit works
          </p>
          <ol className="mt-3 grid md:grid-cols-5 gap-3 text-sm">
            {["Overview", "Workflow", "Recommended tools", "Prompt Builder", "Examples"].map((s, i) => (
              <li key={s} className="rounded-md border border-shell-border p-3">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Section {i + 1}</p>
                <p className="mt-1 font-semibold">{s}</p>
              </li>
            ))}
          </ol>
          <p className="mt-3 text-xs text-muted-foreground">
            Provider-agnostic: the Lab generates prompts calibrated to your brand — you
            run them in your image/video/copy tool of choice.
          </p>
        </section>
      </div>
    </AppShell>
  );
}

function ToolkitCard({ id, title, blurb, to, icon: Icon, status, tag }) {
  return (
    <Link to={to} className="group rounded-2xl border border-shell-border p-5 bg-white hover:border-shell-side transition-colors"
      data-testid={`ai-lab-toolkit-${id}`}>
      <div className="flex items-start justify-between gap-3">
        <span className="w-10 h-10 rounded-xl bg-shell-side/10 text-shell-side grid place-items-center">
          <Icon className="w-5 h-5" />
        </span>
        <span className="text-[9px] uppercase tracking-widest font-bold text-muted-foreground">
          {tag}
        </span>
      </div>
      <p className="mt-4 font-display text-lg">{title}</p>
      <p className="mt-1 text-sm text-muted-foreground">{blurb}</p>
      <p className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-shell-side">
        Open <ChevronRight className="w-3.5 h-3.5" />
      </p>
    </Link>
  );
}
